import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Layers,
  RefreshCw,
  ArrowRight,
  Package,
  Calendar,
  AlertCircle,
  FileCheck,
  Building2,
  Printer,
  Boxes,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { formatNumber, formatDate } from "@/lib/utils";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";

export function PrepressReadyPoolTab() {
  const navigate = useNavigate();
  const { headerSearch } = useHeaderSearch();
  const [hasPrepressOnly, setHasPrepressOnly] = useState(true);

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["prepress-pool", headerSearch, hasPrepressOnly],
    queryFn: async () => {
      return (await trpcClient.productionOrders.getPrepressPool.query({
        search: headerSearch.trim() || undefined,
        hasPrepressOnly: hasPrepressOnly,
      })) as {
        totalGroups: number;
        totalItems: number;
        groups: Array<{
          id: string;
          groupKey: string;
          prepressCode: string | null;
          isPrepressAssigned: boolean;
          paperSize: string | null;
          prepressNotes: string | null;
          customerId: string;
          customerName: string;
          customerCode: string;
          primaryOrderId: string;
          primaryOrderCode: string;
          deliveryDeadline: string | Date | null;
          isGangRun: boolean;
          itemCount: number;
          totalUps: number;
          totalRemainingQty: number;
          totalOrderQty: number;
          calculatedRunSheets: number;
          items: Array<{
            id: string;
            detailId: string;
            orderId: string;
            orderCode: string;
            customerId: string;
            customerName: string;
            productId: string;
            productCode: string;
            productName: string;
            unit: string;
            quantity: number;
            plannedQty: number;
            remainingPlanQty: number;
            prepressUps: number;
            prepressPaperSize: string | null;
            prepressPrintSheets: number;
            deliveryDeadline: string | Date | null;
          }>;
        }>;
      };
    },
  });

  const totalSheetsAll = useMemo(() => {
    if (!data?.groups) return 0;
    return data.groups.reduce((sum, g) => sum + (g.calculatedRunSheets || 0), 0);
  }, [data]);

  const handleCreatePOFromGroup = (group: any) => {
    const detailIds = group.items.map((it: any) => it.id).join(",");
    navigate({
      to: "/production-orders/create" as any,
      search: {
        salesOrderId: group.primaryOrderId,
        layoutCode: group.prepressCode || "",
        poType: group.isGangRun ? "GANG_RUN" : "SINGLE",
        detailIds: detailIds,
        customerId: group.customerId,
      } as any,
    });
  };

  return (
    <div className="p-3 sm:p-4 space-y-3 bg-slate-50/50 min-h-[calc(100vh-140px)]">
      {/* Single-Row Inline Metrics Bar (Rule 4) */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 px-3 py-2 rounded-lg border border-slate-200/80 bg-white shadow-xs text-xs text-slate-600">
        <div className="flex flex-wrap items-center gap-3 font-medium">
          <span className="flex items-center gap-1.5 text-indigo-700 font-semibold">
            <Layers size={14} className="text-indigo-600" />
            Chờ lập lệnh: <strong className="text-indigo-900">{formatNumber(data?.totalGroups || 0)} bài</strong>
          </span>
          <span className="hidden sm:inline text-slate-300">|</span>
          <span className="flex items-center gap-1.5 text-blue-700 font-semibold">
            <Boxes size={14} className="text-blue-600" />
            Mặt hàng: <strong className="text-blue-900">{formatNumber(data?.totalItems || 0)} mã SP</strong>
          </span>
          <span className="hidden sm:inline text-slate-300">|</span>
          <span className="flex items-center gap-1.5 text-emerald-700 font-semibold">
            <Printer size={14} className="text-emerald-600" />
            Tổng tờ in cần chạy: <strong className="text-emerald-900">{formatNumber(totalSheetsAll)} tờ</strong>
          </span>
          {headerSearch && (
            <>
              <span className="hidden sm:inline text-slate-300">|</span>
              <span className="text-slate-500 italic">Tìm kiếm: "{headerSearch}"</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-slate-700 select-none">
            <Checkbox
              checked={hasPrepressOnly}
              onCheckedChange={(checked) => setHasPrepressOnly(!!checked)}
            />
            <span>Chỉ bài đã bình file (Prepress Ready)</span>
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <RefreshCw size={12} className={`mr-1 ${isRefetching ? "animate-spin text-indigo-600" : ""}`} />
            Làm mới
          </Button>
        </div>
      </div>

      {/* 4. Layout Groups Accordion / List */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center p-16 space-y-3 bg-white rounded-xl border border-slate-200/80">
          <RefreshCw size={26} className="animate-spin text-indigo-600" />
          <p className="text-xs text-slate-500 font-medium">Đang tải dữ liệu bài bình từ bể điều độ...</p>
        </div>
      ) : !data?.groups || data.groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-16 space-y-3 bg-white rounded-xl border border-dashed border-slate-200 text-center">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <Package size={24} />
          </div>
          <h3 className="text-sm font-semibold text-slate-800">Không có bài bình nào đang chờ lập lệnh</h3>
          <p className="text-xs text-slate-500 max-w-md">
            Hiện tại tất cả các đơn hàng đã bình file đều đã được lên Lệnh Sản Xuất, hoặc chưa có chi tiết đơn hàng nào được gán mã bài bình.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.groups.map((group) => {
            return (
              <Card
                key={group.id}
                className="bg-white border-slate-200 shadow-xs hover:border-indigo-300 transition-all overflow-hidden"
              >
                {/* Group Header Card */}
                <div className="p-4 bg-gradient-to-r from-slate-50 via-white to-indigo-50/30 border-b border-slate-100">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        {group.isGangRun ? (
                          <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-xs px-2.5 py-0.5 shadow-xs">
                            <Layers size={12} className="mr-1 inline" /> In Ghép Bài ({group.itemCount} mã SP)
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-2.5 py-0.5 shadow-xs">
                            <Package size={12} className="mr-1 inline" /> In Đơn ({group.itemCount} mã SP)
                          </Badge>
                        )}

                        <span className="font-mono font-bold text-sm text-slate-900 bg-slate-100 px-2.5 py-0.5 rounded border border-slate-200/80">
                          {group.prepressCode || "Chưa gắn mã bài"}
                        </span>

                        {group.paperSize && (
                          <Badge variant="outline" className="text-xs bg-white border-slate-200 font-medium text-slate-700">
                            Khổ in: <span className="font-semibold text-slate-900 ml-1">{group.paperSize}</span>
                          </Badge>
                        )}

                        <Badge variant="outline" className="text-xs bg-white border-indigo-200 font-medium text-indigo-700">
                          Tổng: <span className="font-bold ml-1">{group.totalUps} UP</span>
                        </Badge>

                        <Badge variant="outline" className="text-xs bg-white border-emerald-200 font-medium text-emerald-700">
                          Chạy: <span className="font-bold ml-1">{formatNumber(group.calculatedRunSheets)} tờ in</span>
                        </Badge>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Building2 size={13} className="text-slate-400" />
                          Khách hàng: <strong className="text-slate-800 font-semibold">{group.customerName}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          <FileCheck size={13} className="text-slate-400" />
                          Đơn hàng: <strong className="text-slate-800 font-semibold">{group.primaryOrderCode}</strong>
                        </span>
                        {group.deliveryDeadline && (
                          <span className="flex items-center gap-1">
                            <Calendar size={13} className="text-slate-400" />
                            Hạn giao: <strong className="text-amber-700 font-medium">{formatDate(group.deliveryDeadline)}</strong>
                          </span>
                        )}
                        {group.prepressNotes && (
                          <span className="text-slate-500 italic max-w-md truncate">
                            "{group.prepressNotes}"
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start lg:self-center">
                      <Button
                        size="sm"
                        onClick={() => handleCreatePOFromGroup(group)}
                        className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs shadow-sm hover:shadow transition-all"
                      >
                        <Sparkles size={14} className="mr-1.5 text-indigo-200" />
                        Lập Lệnh Sản Xuất F0
                        <ArrowRight size={14} className="ml-1.5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sub-table of Items in Layout */}
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50/70 border-b border-slate-100 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          <th className="py-2.5 px-4">Mã Sản Phẩm</th>
                          <th className="py-2.5 px-4">Tên Sản Phẩm</th>
                          <th className="py-2.5 px-3">Mã Đơn Hàng</th>
                          <th className="py-2.5 px-3 text-right">SL Đặt</th>
                          <th className="py-2.5 px-3 text-right">Đã Lập Lệnh</th>
                          <th className="py-2.5 px-3 text-right">Chờ Lên Lệnh</th>
                          <th className="py-2.5 px-3 text-center">Số Ups</th>
                          <th className="py-2.5 px-3 text-right">Tờ In Riêng Mã</th>
                          <th className="py-2.5 px-4 text-center">Hạn Giao</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {group.items.map((it: any) => (
                          <tr key={it.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2.5 px-4 font-mono font-medium text-slate-700">
                              {it.productCode || "—"}
                            </td>
                            <td className="py-2.5 px-4 font-semibold text-slate-900 max-w-[280px] truncate">
                              {it.productName}
                            </td>
                            <td className="py-2.5 px-3 font-mono text-slate-600">
                              {it.orderCode}
                            </td>
                            <td className="py-2.5 px-3 text-right font-medium text-slate-700">
                              {formatNumber(it.quantity)} <span className="text-[10px] text-slate-400">{it.unit}</span>
                            </td>
                            <td className="py-2.5 px-3 text-right text-slate-500">
                              {formatNumber(it.plannedQty || 0)}
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-bold px-1.5 py-0">
                                {formatNumber(it.remainingPlanQty)}
                              </Badge>
                            </td>
                            <td className="py-2.5 px-3 text-center font-semibold text-indigo-700">
                              {it.prepressUps} UP
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                              {formatNumber(it.prepressPrintSheets || 0)}
                            </td>
                            <td className="py-2.5 px-4 text-center text-slate-600">
                              {it.deliveryDeadline ? formatDate(it.deliveryDeadline) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default PrepressReadyPoolTab;
